import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { authAPI } from '../../../services/api';
import { useUserRedux } from '../../../hooks/useUserRedux';
import { setDailyRewardModal } from '../../../store/slices/uiSlice';
import { fetchAllShopItems, fetchShopPacks } from '../../shop/store/shopSlice';
import { fetchTicketStatus } from '../../tickets/store/ticketsSlice';
import { fetchRewards } from '../../spin/store/rewardsSlice';
import logger from '../../../core/logger/logger';
import errorNotification from '../../../core/error/errorNotification';

/**
 * Хук для управления авторизацией пользователя
 * 
 * Обрабатывает:
 * - Получение initData из Telegram WebApp
 * - Авторизацию через API
 * - Сохранение токенов
 * - Обновление пользователя в Redux
 * - Обработку ежедневных наград
 */
export const useAuth = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const { setUser: setUserRedux } = useUserRedux();
  const dispatch = useDispatch();

  /**
   * Получить initData из Telegram WebApp
   */
  const getTelegramInitData = () => {
    const initDataRaw = window?.Telegram?.WebApp?.initData;
    console.log()
    if (!initDataRaw) {
      logger.warn('Telegram initData not found');
      return null;

    }

    // Парсим initData для получения данных пользователя (для логирования)
    const urlParams = new URLSearchParams(initDataRaw);
    const userParam = urlParams.get('user');

    if (!userParam) {
      logger.warn('User data not found in initData');
      return null;
    }

    try {
      const userData = JSON.parse(userParam);
      logger.debug('Telegram user data parsed', { userId: userData.id });
      return initDataRaw;
    } catch (error) {
      logger.error('Failed to parse user data from initData', error);
      return null;
    }
  };

  /**
   * Автоматическая авторизация при монтировании компонента
   */
  useEffect(() => {
    let isMounted = true;

    const initAuth = async () => {
      try {
        setLoading(true);
        setError(null);

        // Получаем initData из Telegram WebApp
        const initDataRaw = getTelegramInitData();
        if (!initDataRaw) {
          if (isMounted) setLoading(false);
          return;
        }

        // Авторизуемся через API
        logger.info('Authenticating with Telegram');
        const authResponse = await authAPI.loginWithTelegram({ initData: initDataRaw });

        // Проверяем, что компонент еще смонтирован
        if (!isMounted) return;

        // Проверяем наличие токена
        if (!authResponse.accessToken) {
          logger.warn('No access token received');
          setLoading(false);
          return;
        }

        // Сохраняем токены
        localStorage.setItem('accessToken', authResponse.accessToken);
        if (authResponse.refreshToken) {
          localStorage.setItem('refreshToken', authResponse.refreshToken);
        }
        localStorage.setItem('userId', authResponse.user.id);

        // Обновляем пользователя в Redux с учетом статуса онбординга
        const userData = {
          ...authResponse.user,
          onboarding: authResponse.userOnboarding
        };
        setUserRedux(userData);
        logger.info('User authenticated successfully', { userId: authResponse.user.id, onboarding: authResponse.userOnboarding });

        // Обрабатываем ежедневную награду
        if (authResponse.showDailyReward) {
          logger.info('Showing daily reward modal after login');
          dispatch(setDailyRewardModal(true));
        }

        // Загружаем товары магазина после успешного логина
        logger.debug('Loading shop items after login');
        dispatch(fetchAllShopItems());

        // Загружаем паки магазина после успешного логина
        logger.debug('Loading shop packs after login');
        dispatch(fetchShopPacks());

        // Загружаем статус тикетов после успешного логина
        logger.debug('Loading ticket status after login');
        dispatch(fetchTicketStatus());

        // Загружаем награды рулетки после успешного логина
        logger.debug('Loading rewards after login');
        dispatch(fetchRewards('base'));

        if (isMounted) setLoading(false);

      } catch (error) {
        if (!isMounted) return;

        logger.error('Authentication failed', error);
        setError(error);
        setLoading(false);

        // Если это ошибка авторизации, показываем уведомление
        if (error.isAuthError || error.response?.status === 401) {
          errorNotification.showApiError(error);
        }
      }
    };

    initAuth();

    return () => {
      isMounted = false;
    };
  }, [retryCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const retry = () => {
    setError(null);
    setLoading(true);
    setRetryCount(c => c + 1);
  };

  return {
    loading,
    error,
    retry,
    isAuthenticated: !loading && !error,
  };
};

